import { ProjectConstructionType } from '@consigli/types';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { RichCheckboxMark } from '@/components/rich-checkbox-mark';

/**
 * The frame's "Rich Checkbox Group" card: 12px padding and gap, a 10px radius,
 * a hairline edge on the surface colour.
 *
 * The edge is an inset ring rather than a `border`, which is what makes the card
 * 44px tall as the frame draws it. Figma strokes a frame *inside* its box, so
 * its 44 is 12 + a 20px line + 12 with the stroke painted within; a CSS border
 * is added outside the padding box and would make the same card 46. The ring is
 * the faithful translation, and it keeps the padding on a token rather than
 * shaving it to 11px to absorb the difference.
 */
const CARD =
  'flex items-start gap-[var(--ds-space-3)] rounded-[var(--ds-radius-10)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-3)] text-left shadow-[inset_0_0_0_1px_var(--ds-color-border-primary)]';

/** 14/20 regular — the frame's `paragraph small/regular`. */
const CARD_LABEL = 'flex-1 text-[14px] leading-5 text-[var(--ds-color-text-primary)]';

interface ConstructionTypeFieldProps {
  disabled?: boolean;
  onChange: (next: ProjectConstructionType[]) => void;
  value: ProjectConstructionType[];
}

/**
 * The project's construction types, as the frame's grid of checkbox cards.
 *
 * Nine cards in two columns, in enum order read row by row, which is exactly
 * the order the frame lays them out — so the source of truth for the order is
 * the enum and there is nothing to keep in step by hand. Deliberately *not*
 * sorted by translated label: the frame's order is the same in every locale.
 *
 * Real `<input type="checkbox">` elements, visually hidden, rather than divs
 * with `role="checkbox"`. The card is the label, so a click anywhere on it
 * toggles, the group is reachable and operable by keyboard for free, and the
 * checked state is the input's own rather than something ARIA has to be told
 * about.
 *
 * Built here rather than taken from the design system, which ships no checkbox
 * at 2.3.0 — `Rich Checkbox Group` exists in Figma and not in the library.
 * Worth raising upstream, as the pill switcher was.
 *
 * The project Settings tab's field, and only its: 11759:160478 draws this flat
 * grid, while the create project form has moved to the grouped cards of
 * 11971:210827. What the two still share is the round mark, which is why that
 * is in components/ and this is not.
 */
export const ConstructionTypeField: FC<ConstructionTypeFieldProps> = ({
  disabled = false,
  onChange,
  value,
}) => {
  const { t } = useTranslation();
  const selected = new Set(value);

  const toggle = (type: ProjectConstructionType) => {
    const next = new Set(selected);

    if (next.has(type)) next.delete(type);
    else next.add(type);

    // Emitted in enum order rather than click order, so the value does not
    // depend on how the user got there and a no-op reorder cannot look dirty.
    onChange(Object.values(ProjectConstructionType).filter((member) => next.has(member)));
  };

  return (
    <div className="grid grid-cols-2 gap-[var(--ds-space-2)]">
      {Object.values(ProjectConstructionType).map((type) => {
        const isSelected = selected.has(type);

        return (
          <label
            className={`${CARD} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            key={type}
          >
            <input
              checked={isSelected}
              className="sr-only"
              disabled={disabled}
              onChange={() => toggle(type)}
              type="checkbox"
            />
            {/* pt-[2.5px] is the frame's own optical nudge, aligning the mark to
                the first line of the label rather than to the card's top. */}
            <span className="flex shrink-0 items-center pt-[2.5px]">
              <RichCheckboxMark checked={isSelected} />
            </span>
            <span className={CARD_LABEL}>{t(`project-construction-type.${type}`)}</span>
          </label>
        );
      })}
    </div>
  );
};
